/**
 * SKUTab — manage CatalogSystemVariants (SKUs, motors, protocols, colors)
 * Each variant = one SKU line with motor_model, protocol, interface_type, color, voltage_type
 */
import { useState, useCallback, useEffect } from 'react';
import { Plus, Trash2, Check, X, ChevronDown, ChevronUp, Loader2, AlertCircle, Barcode } from 'lucide-react';
import api from '@/api';
import type { AdminCatalogSystem } from '@/api';

interface SKUVariant {
  id?: number;
  sku: string;
  motor_model: string | null;
  protocol: string | null;
  interface_type: string | null;
  color: string | null;
  voltage_type: string | null;
  stock_qty: number;
  is_active: boolean;
  subtype_tags: string[] | null;
}

interface SKUTabProps {
  system: AdminCatalogSystem;
}

const PROTOCOLS = ['RS-485', 'Wi-Fi (MiHome)', 'Wi-Fi (TUYA)', 'FC', 'ZigBee', 'Matter'];
const INTERFACE_TYPES = ['smart', 'rs485', 'phase', 'phase_rs485', 'dry_contact', 'battery'];
const VOLTAGE_TYPES = ['220V', 'АКБ', '12V', '24V'];

const INTERFACE_LABELS: Record<string, string> = {
  smart: 'Smart',
  rs485: 'RS-485',
  phase: 'Phase',
  phase_rs485: 'Phase+RS-485',
  dry_contact: 'Dry Contact',
  battery: 'АКБ',
};

function VariantRow({
  variant,
  onSave,
  onDelete,
  isNew,
}: {
  variant: SKUVariant;
  onSave: (v: SKUVariant) => Promise<void>;
  onDelete: () => Promise<void>;
  isNew?: boolean;
}) {
  const [v, setV] = useState<SKUVariant>(variant);
  const [expanded, setExpanded] = useState(isNew ?? false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dirty, setDirty] = useState(isNew ?? false);
  const [saved, setSaved] = useState(false);

  const update = <K extends keyof SKUVariant>(field: K, value: SKUVariant[K]) => {
    setV((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(v);
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={`rounded-xl border transition-all ${
      v.is_active ? 'bg-white/[0.03] border-white/[0.08]' : 'bg-white/[0.01] border-white/[0.04] opacity-60'
    }`}>
      {/* Header row — always visible */}
      <div className="flex items-center gap-3 px-3 py-2.5">
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-white/40 hover:text-white/70 transition-colors"
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {/* SKU badge */}
        <div className="flex items-center gap-1.5 min-w-0">
          <Barcode size={12} className="text-white/30 shrink-0" />
          <input
            type="text"
            value={v.sku}
            onChange={(e) => update('sku', e.target.value)}
            className="w-40 px-2 py-1 rounded-lg text-xs bg-white/[0.04] border border-white/[0.06] focus:border-[#68D391]/40 focus:outline-none text-white/90 font-mono"
            placeholder="SKU"
          />
        </div>

        {/* Quick view tags */}
        <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden">
          {v.motor_model && (
            <span className="px-2 py-0.5 rounded text-[10px] bg-blue-500/15 text-blue-400 whitespace-nowrap">
              {v.motor_model}
            </span>
          )}
          {v.protocol && (
            <span className="px-2 py-0.5 rounded text-[10px] bg-purple-500/15 text-purple-400 whitespace-nowrap">
              {v.protocol}
            </span>
          )}
          {v.color && (
            <span className="px-2 py-0.5 rounded text-[10px] bg-amber-500/15 text-amber-400 whitespace-nowrap">
              {v.color}
            </span>
          )}
        </div>

        {/* Active toggle */}
        <button
          onClick={() => update('is_active', !v.is_active)}
          className={`px-2 py-1 rounded-lg text-[11px] font-medium transition-all ${
            v.is_active
              ? 'bg-[#68D391]/10 text-[#68D391]'
              : 'bg-white/[0.04] text-white/40'
          }`}
        >
          {v.is_active ? '✓ Активен' : '✗ Скрыт'}
        </button>

        {/* Save button */}
        {dirty && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-[#68D391] text-[#1a202c] disabled:opacity-50 transition-all"
          >
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
            {saving ? '...' : 'Сохранить'}
          </button>
        )}
        {saved && !dirty && (
          <span className="text-[11px] text-[#68D391] flex items-center gap-1">
            <Check size={11} /> Сохранено
          </span>
        )}

        {/* Delete */}
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="p-1.5 rounded-lg hover:bg-red-500/10 text-white/30 hover:text-red-400 transition-colors shrink-0"
        >
          {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
        </button>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-white/[0.05] grid grid-cols-2 gap-3">
          {/* Motor Model */}
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wide text-white/40">Модель мотора</label>
            <input
              type="text"
              value={v.motor_model ?? ''}
              onChange={(e) => update('motor_model', e.target.value || null)}
              className="w-full px-2.5 py-1.5 rounded-lg text-xs bg-white/[0.04] border border-white/[0.06] focus:border-blue-400/40 focus:outline-none text-white"
              placeholder="MR-2234, MR-350..."
            />
          </div>

          {/* Protocol */}
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wide text-white/40">Протокол</label>
            <select
              value={v.protocol ?? ''}
              onChange={(e) => update('protocol', e.target.value || null)}
              className="w-full px-2.5 py-1.5 rounded-lg text-xs bg-white/[0.06] border border-white/[0.06] focus:border-purple-400/40 focus:outline-none text-white"
            >
              <option value="">— не указан —</option>
              {PROTOCOLS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* Interface Type */}
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wide text-white/40">Тип интерфейса</label>
            <select
              value={v.interface_type ?? ''}
              onChange={(e) => update('interface_type', e.target.value || null)}
              className="w-full px-2.5 py-1.5 rounded-lg text-xs bg-white/[0.06] border border-white/[0.06] focus:border-white/20 focus:outline-none text-white"
            >
              <option value="">— не указан —</option>
              {INTERFACE_TYPES.map((i) => (
                <option key={i} value={i}>{INTERFACE_LABELS[i] ?? i}</option>
              ))}
            </select>
          </div>

          {/* Color */}
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wide text-white/40">Цвет</label>
            <input
              type="text"
              value={v.color ?? ''}
              onChange={(e) => update('color', e.target.value || null)}
              className="w-full px-2.5 py-1.5 rounded-lg text-xs bg-white/[0.04] border border-white/[0.06] focus:border-amber-400/40 focus:outline-none text-white"
              placeholder="Белый, Черный..."
            />
          </div>

          {/* Voltage Type */}
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wide text-white/40">Питание</label>
            <select
              value={v.voltage_type ?? ''}
              onChange={(e) => update('voltage_type', e.target.value || null)}
              className="w-full px-2.5 py-1.5 rounded-lg text-xs bg-white/[0.06] border border-white/[0.06] focus:border-white/20 focus:outline-none text-white"
            >
              <option value="">— не указан —</option>
              {VOLTAGE_TYPES.map((vt) => (
                <option key={vt} value={vt}>{vt}</option>
              ))}
            </select>
          </div>

          {/* Stock qty */}
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wide text-white/40">Остаток</label>
            <input
              type="number"
              value={v.stock_qty}
              onChange={(e) => update('stock_qty', parseInt(e.target.value) || 0)}
              className="w-full px-2.5 py-1.5 rounded-lg text-xs bg-white/[0.04] border border-white/[0.06] focus:border-white/20 focus:outline-none text-white"
              min={0}
            />
          </div>

          {/* Subtype tags */}
          <div className="col-span-2 space-y-1">
            <label className="text-[10px] uppercase tracking-wide text-white/40">
              Теги подтипов (через запятую)
            </label>
            <input
              type="text"
              value={(v.subtype_tags ?? []).join(', ')}
              onChange={(e) =>
                update('subtype_tags', e.target.value ? e.target.value.split(',').map((s) => s.trim()) : [])
              }
              className="w-full px-2.5 py-1.5 rounded-lg text-xs bg-white/[0.04] border border-white/[0.06] focus:border-white/20 focus:outline-none text-white"
              placeholder="direct, radial, premium..."
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function SKUTab({ system }: SKUTabProps) {
  const [variants, setVariants] = useState<SKUVariant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<SKUVariant[]>(`/admin/catalog-systems/${system.id}/variants`);
      setVariants(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('Ошибка загрузки артикулов');
    } finally {
      setLoading(false);
    }
  }, [system.id]);

  useEffect(() => { void load(); }, [load]);

  const handleSave = async (updated: SKUVariant) => {
    if (updated.id) {
      await api.patch(`/admin/catalog-systems/${system.id}/variants/${updated.id}`, updated);
      setVariants((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
    } else {
      const res = await api.post<{ id: number; sku: string }>(
        `/admin/catalog-systems/${system.id}/variants`,
        updated
      );
      setVariants((prev) => prev.map((v) => (!v.id && v.sku === updated.sku ? { ...updated, id: res.data.id } : v)));
    }
  };

  const handleDelete = async (variant: SKUVariant) => {
    if (variant.id) {
      await api.delete(`/admin/catalog-systems/${system.id}/variants/${variant.id}`);
    }
    setVariants((prev) => prev.filter((v) => v.id !== variant.id || (!v.id && v.sku !== variant.sku)));
  };

  const handleAdd = () => {
    const newVariant: SKUVariant = {
      sku: `${system.slug}-${Date.now().toString(36).toUpperCase()}`,
      motor_model: null,
      protocol: null,
      interface_type: null,
      color: null,
      voltage_type: '220V',
      stock_qty: 0,
      is_active: true,
      subtype_tags: [],
    };
    setVariants((prev) => [newVariant, ...prev]);
  };

  const filtered = filterText
    ? variants.filter(
        (v) =>
          v.sku.toLowerCase().includes(filterText.toLowerCase()) ||
          (v.motor_model ?? '').toLowerCase().includes(filterText.toLowerCase()) ||
          (v.protocol ?? '').toLowerCase().includes(filterText.toLowerCase()) ||
          (v.color ?? '').toLowerCase().includes(filterText.toLowerCase())
      )
    : variants;

  const activeCount = variants.filter((v) => v.is_active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-white/40">
        <Loader2 size={20} className="animate-spin mr-2" />
        Загрузка артикулов...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#68D391]/10">
            <Barcode size={18} className="text-[#68D391]" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-white/90">Артикулы (SKU)</h3>
            <p className="text-xs text-white/40">{activeCount} активных из {variants.length}</p>
          </div>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-[#68D391]/15 text-[#68D391] hover:bg-[#68D391]/25 transition-colors"
        >
          <Plus size={14} />
          Новый артикул
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-2 text-xs bg-orange-500/10 text-orange-400 rounded-xl">
          <AlertCircle size={14} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto hover:text-orange-300"><X size={12} /></button>
        </div>
      )}

      {/* Filter */}
      {variants.length > 5 && (
        <input
          type="text"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder="Фильтр по SKU, мотору, протоколу, цвету..."
          className="w-full px-3 py-2 rounded-xl text-xs bg-white/[0.04] border border-white/[0.06] focus:border-white/20 focus:outline-none text-white placeholder-white/30"
        />
      )}

      {/* Variants list */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="text-center py-10 text-sm text-white/40">
            {variants.length === 0
              ? 'Нет артикулов. Нажмите «Новый артикул» чтобы добавить.'
              : 'Ничего не найдено по фильтру.'}
          </div>
        ) : (
          filtered.map((variant, idx) => (
            <VariantRow
              key={variant.id ?? `new-${idx}`}
              variant={variant}
              isNew={!variant.id}
              onSave={handleSave}
              onDelete={() => handleDelete(variant)}
            />
          ))
        )}
      </div>

      {/* Stats footer */}
      {variants.length > 0 && (
        <div className="flex flex-wrap gap-3 pt-2 border-t border-white/[0.06]">
          {['motor_model', 'protocol', 'color'].map((field) => {
            const vals = [...new Set(variants.map((v) => (v as unknown as Record<string, unknown>)[field] as string).filter(Boolean))];
            return vals.length > 0 ? (
              <div key={field} className="text-xs text-white/40">
                <span className="text-white/60 font-medium">
                  {field === 'motor_model' ? 'Моторы' : field === 'protocol' ? 'Протоколы' : 'Цвета'}:{' '}
                </span>
                {vals.slice(0, 4).join(', ')}{vals.length > 4 ? ` +${vals.length - 4}` : ''}
              </div>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
}
